import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  inject,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule, Location } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SidebarComponent } from '../../sidebars/sidebar/sidebar.component';
import {
  DragDropModule,
  CdkDragDrop,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { Router, ActivatedRoute } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { map, catchError, take } from 'rxjs/operators';

import { CoreService } from '../../../services/core.service';
import {
  SocialPlatformI,
  ContentSiteI,
  RssFeedI,
} from '../../../interfaces/core.interface';
import { DialogComponent } from '../../dialog/dialog.component';

type Post = {
  id: number;
  rss_title: string;
  domain_name?: string;
  image_url: string;
  post_status?: string;
  enable_repeat?: number;
  parent_post_id?: number;
  tag?: string;
  social_platform_title?: string;
  scheduled_for?: string | Date;
  rss_description?: string;
};

type Alert = {
  id: number;
  contentsite_id: number;
  alert_text: string;
  alert_type: string;
  active: number;
  platform: string;
  format: string;
  rss_img_url: string;
  rss_title: string;
  created_at: string | Date;
  Domain: string;
  posting_queue_id: number;
};



@Component({
  selector: 'app-social-ai-edit-post-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    DragDropModule,
    MatTooltipModule,
    MatDialogModule,
    MatCheckboxModule,
    SidebarComponent,
    DialogComponent
  ],
  templateUrl: './social-ai-edit-post-form.component.html',
  styleUrl: './social-ai-edit-post-form.component.scss',
})
export class SocialAiEditPostFormComponent implements OnInit {
  @ViewChild('confirmDeleteTmpl', { static: false }) confirmDeleteTmpl!: TemplateRef<any>;
  @Input() formData: any = {} as any;
  @Output() onSubmitClbk = new EventEmitter<any>();

  postForm!: FormGroup;
  isDataLoaded = false;
  companyID = 0;

  sites: ContentSiteI[] = [];
  platforms: SocialPlatformI[] = [];
  feeds: RssFeedI[] = [];
  filteredPlatforms: SocialPlatformI[] = [];
  filteredTags: string[] = [];
  filteredPostStatuses: { value: string; label: string }[] = [];
  postStatus = '';
  enableRepeat: number | null = null;
  enableDelete = 1;

  id: string | null = null;
  selectedFile: File | null = null;
  imagePreview: string | null = null;
  uploadedFiles: { file: File | null; preview: string; alt: string }[] = [];
  private previousTag: string | null = null;
  urlsArry: string[] = [];
  videoFileName: string | null = null;

  nextId: string | null = null;
  prevId: string | null = null;

  // side panel
  maxSidebarCards = 3;
  posts: Post[] = [];
  alerts: Alert[] = [];
  naPlaceholder = './assets/images/na.png';
  private readonly LATEST_POSTS_KEY = 'latestPosts';
  private readonly LATEST_ALERTS_KEY = 'latestAlerts';

  // text direction
  textDirection: 'ltr' | 'rtl' = 'ltr';
  setTextDirection(dir: 'ltr' | 'rtl') {
    this.textDirection = dir;
  }

  private readonly MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
  private readonly URL_PATTERN =
    /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/\S*)?$/;
  private readonly YOUTUBE_PATTERN =
    /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[A-Za-z0-9_-]{11}(&.*)?$/;

  public dialog = inject(MatDialog);

  get isDeleteDisabled(): boolean {
    return false;
  }

  get isEditBlocked(): boolean {
    return (
      ['PostedOnInstagram', 'PostedOnFacebook'].includes(
        this.postStatus || ''
      ) || this.enableRepeat === -1
    );
  }

  displayTagMap: Record<string, string> = {
    'Facebook Link Post': 'photolink',
    'Facebook Photo Post': 'fbphotopost',
    'Facebook Photo Post (1st comment)': 'fbfirstcomment',
    'Facebook Video Post': 'fbvideo',
    'Facebook Reel Post': 'fbreel',
    'Facebook Story Photo': 'fbstoryphoto',
    'Facebook Story Video': 'fbstoryvideo',
    'Facebook Story Reel': 'fbstoryreel',
    'Instagram Photo Post': 'igphotopost',
    'Instagram Video Post': 'igvideo',
    'Instagram Reel Post': 'igreel',
    'Instagram Carousel Post': 'igcarousel',
    'Instagram Story Video': 'igstoryvideo',
    'Instagram Story Photo': 'igstoryphoto',
    'Instagram Story Reel': 'igstoryreel',
    'TikTok Video Post': 'tiktokvideo',
    'TikTok Reel Post': 'tiktokreel',
    'TikTok Carousel Post': 'tiktokcarousel',
    'TikTok Story Photo': 'tiktokstoryphoto',
    'TikTok Story Video': 'tiktokstoryvideo',
    'TikTok Story Reel': 'tiktokstoryreel',
  };

  videoStoryTags = [
    'fbvideo',
    'igvideo',
    'fbstoryvideo',
    'igstoryvideo',
    'tiktokstoryvideo',
    'tiktokvideo',
    'fbreel',
    'fbstoryreel',
    'igreel',
    'igstoryreel',
    'tiktokreel',
    'tiktokstoryreel',
  ];

  mediaTags = [
    'igcarousel',
    'tiktokcarousel',
    'fbreel',
    'igreel',
    'tiktokreel',
    'fbstoryreel',
    'igstoryreel',
    'tiktokstoryreel',
  ];

  singleImageTags = [
    'fbphotopost',
    'fbfirstcomment',
    'fbstoryphoto',
    'igphotopost',
    'igstoryphoto',
    'tiktokstoryphoto',
  ];

  fbTags = [
    'fbphotopost',
    'fbfirstcomment',
    'photolink',
    'fbvideo',
    'fbreel',
    'fbstoryphoto',
    'fbstoryvideo',
    'fbstoryreel',
  ];

  igTags = [
    'igphotopost',
    'igcarousel',
    'igvideo',
    'igreel',
    'igstoryphoto',
    'igstoryvideo',
    'igstoryreel',
  ];

  tiktokTags = [
    'tiktokcarousel',
    'tiktokstoryphoto',
    'tiktokvideo',
    'tiktokreel',
    'tiktokstoryvideo',
    'tiktokstoryreel',
  ];

  allVideoTags = [
    'fbvideo',
    'igvideo',
    'tiktokvideo',
    'fbstoryvideo',
    'igstoryvideo',
    'tiktokstoryvideo',
  ];

  reelTags = ['igreel', 'fbreel', 'tiktokreel'];

  postStatuses = [
    { value: 'Scheduled', label: 'Scheduled' },
    { value: 'ScheduledToPostNow', label: 'Scheduled To Post Now' },
    { value: 'Draft', label: 'Draft' },
    { value: 'ScheduledToFbStory', label: 'Scheduled To Facebook Story' },
    { value: 'ScheduledToInstaStory', label: 'Scheduled To Instagram Story' },
    { value: 'ScheduledToTikTokStory', label: 'Scheduled To TikTok Story' },
  ];

  disableScheduleStatuses = [
    'Draft',
    'ScheduledToPostNow',
    'ScheduledToFbStory',
    'ScheduledToInstaStory',
    'ScheduledToTikTokStory',
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly coreService: CoreService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly location: Location
  ) {}

  ngOnInit(): void {
    this.companyID = this.coreService.user?.companyID || 0;

    if (!this.companyID) {
      this.coreService.showSnackbar('No company found.');
      return;
    }

    this.initForm();

    forkJoin({
      sites: this.loadSites(),
      platforms: this.loadPlatforms(),
    }).subscribe(({ sites, platforms }) => {
      this.sites = sites;
      this.platforms = platforms;

      this.initValueChanges();

      this.route.paramMap.subscribe((params) => {
        this.id = params.get('id');

        this.isDataLoaded = false;
        this.imagePreview = null;
        this.selectedFile = null;
        this.uploadedFiles = [];

        if (this.id) {
          this.loadPost(this.id);
          this.loadActions(this.id);

          this.postForm.get('site')?.disable();
          this.postForm.get('rss_feed_id')?.disable();
          this.postForm.get('platform')?.disable();
          this.postForm.get('tag')?.disable();
        } else {
          this.postForm.enable();
          this.isDataLoaded = true;
        }
      });
    });

    this.loadSidebarPosts();
    this.loadAlertsFromStorage();
  }

  private initForm(): void {
    this.postForm = this.fb.group({
      site: ['', Validators.required],
      rss_feed_id: [null, Validators.required],
      platform: ['', Validators.required],
      content_site_id: [''],
      post_status: ['', Validators.required],
      scheduled_for: [null],
      post_title: ['', [Validators.required, Validators.maxLength(255)]],
      tag: ['', Validators.required],
      rss_article_url: [
        '',
        [
          Validators.required,
          Validators.pattern(this.URL_PATTERN),
        ],
      ],
      rss_description: ['', [Validators.required, Validators.maxLength(902)]],
      image_input: [''],
      image_array: [''],
      alt_text_array: [''],
      social_platform_id: [null],
      is_altai: [false],
      video_type: [''],
      video_type_id: [null],
      video_file: [null],
      video_url: [''],
    });

    this.postForm
      .get('post_status')
      ?.valueChanges.subscribe((status: string) => {
        this.updateScheduledForState(status);
      });

    this.postForm.get('video_type')?.valueChanges.subscribe((type) => {
      if (this.id) return;

      this.postForm.patchValue({
        video_file: null,
        video_url: '',
      });

      this.postForm.get('video_file')?.clearValidators();
      this.postForm.get('video_url')?.clearValidators();

      if (type === 'video_upload') {
        this.postForm.get('video_file')?.setValidators([Validators.required]);
      }

      if (type === 'video_url') {
        this.postForm.get('video_url')?.setValidators([
          Validators.required,
          Validators.pattern(this.YOUTUBE_PATTERN),
        ]);
      }

      this.postForm.get('video_file')?.updateValueAndValidity();
      this.postForm.get('video_url')?.updateValueAndValidity();
    });
  }

  private updateScheduledForState(status: string): void {
    const scheduledForControl = this.postForm.get('scheduled_for');
    if (this.disableScheduleStatuses.includes(status)) {
      scheduledForControl?.setValue(null);
      scheduledForControl?.disable();
    } else {
      scheduledForControl?.enable();
    }
    scheduledForControl?.updateValueAndValidity();
  }

  getDisplayTagLabel(tag: string): string {
    const entry = Object.entries(this.displayTagMap).find(
      ([, value]) => value === tag
    );
    return entry ? entry[0] : tag;
  }

  private filterPostStatusesByTag(tag: string): void {
    if (tag === 'igstoryphoto' || tag === 'igstoryvideo') {
      this.filteredPostStatuses = this.postStatuses.filter(
        (s) => s.value === 'ScheduledToInstaStory'
      );
      this.postForm
        .get('post_status')
        ?.setValue('ScheduledToInstaStory', { emitEvent: false });
    } else if (tag === 'fbstoryphoto' || tag === 'fbstoryvideo') {
      this.filteredPostStatuses = this.postStatuses.filter(
        (s) => s.value === 'ScheduledToFbStory'
      );
      this.postForm
        .get('post_status')
        ?.setValue('ScheduledToFbStory', { emitEvent: false });
    } else if (tag === 'tiktokstoryphoto' || tag === 'tiktokstoryvideo') {
      this.filteredPostStatuses = this.postStatuses.filter(
        (s) => s.value === 'ScheduledToTikTokStory'
      );
      this.postForm
        .get('post_status')
        ?.setValue('ScheduledToTikTokStory', { emitEvent: false });
    } else {
      this.filteredPostStatuses = this.postStatuses.filter(
        (s) =>
          ![
            'ScheduledToFbStory',
            'ScheduledToInstaStory',
            'ScheduledToTikTokStory',
          ].includes(s.value)
      );
      this.postForm.get('post_status')?.reset('', { emitEvent: false });
    }

    this.updateScheduledForState(
      this.postForm.get('post_status')?.value || ''
    );
  }

  private setRssUrlValidators(tag: string): void {
    const rssUrlControl = this.postForm.get('rss_article_url');

    if (tag === 'photolink') {
      rssUrlControl?.setValidators([
        Validators.required,
        Validators.pattern(this.URL_PATTERN),
      ]);
    } else {
      rssUrlControl?.setValidators([
        Validators.pattern(this.URL_PATTERN),
      ]);
    }

    rssUrlControl?.updateValueAndValidity({ emitEvent: false });
  }

  private updateFilteredTagsByPlatform(platform: string): void {
    const p = (platform || '').toLowerCase();
    if (p.includes('facebook')) this.filteredTags = this.fbTags;
    else if (p.includes('instagram')) this.filteredTags = this.igTags;
    else if (p.includes('tiktok')) this.filteredTags = this.tiktokTags;
    else this.filteredTags = [];
  }

  loadPost(id: string): void {
    this.coreService
      .getData(`/manual-post/editPost/${this.companyID}/${id}`)
      .pipe(take(1))
      .subscribe((res: any) => {
        if (!res) {
          this.coreService.showSnackbar('Post not found.');
          return;
        }
        console.log(res);
        this.textDirection = res.text_direction === 'rtl' ? 'rtl' : 'ltr';

        const videoTypeMapping: any = {
          Upload: 'video_upload',
          YouTube: 'video_url',
        };

        const mappedType =
          videoTypeMapping[res.video_type_name] || res.video_type_name || '';

        if (mappedType === 'video_upload' && res.video_url) {
          this.videoFileName = res.video_url.split('/').pop()!;
        }

        this.filteredPlatforms = this.platforms.filter(
          (p) => p.site_id === res.content_site_id
        );

        const selectedPlatform = this.filteredPlatforms.find(
          (p: SocialPlatformI) => p.platform === res.platform
        );

        this.updateFilteredTagsByPlatform(res.platform || '');
        this.filterPostStatusesByTag(res.social_tag || '');

        let scheduledValue: string | null = null;

        if (
          res.scheduled_for &&
          ![
            'ScheduledToFbStory',
            'ScheduledToInstaStory',
            'ScheduledToTikTokStory',
            'ScheduledToPostNow',
          ].includes(res.post_status)
        ) {
          const d = new Date(res.scheduled_for);
          scheduledValue = new Date(
            d.getTime() - d.getTimezoneOffset() * 60000
          )
            .toISOString()
            .slice(0, 16);
        }

        this.postForm.patchValue(
          {
            site: res.Domain || '',
            content_site_id: res.content_site_id || '',
            rss_feed_id: res.rss_feed_id || null,
            platform: selectedPlatform?.platform || '',
            social_platform_id: selectedPlatform?.id || null,
            tag: res.social_tag || '',
            rss_description: res.rss_description || '',
            post_title: res.rss_title || '',
            rss_article_url: res.article_url || '',
            post_status: res.post_status || '',
            scheduled_for: scheduledValue,
            alt_text_array: res.alt_text_array,
            is_altai: !!res.is_altai,
            video_type: mappedType,
            video_type_id: res.video_type_id ?? null,
            video_url: res.video_url || '',
          },
          { emitEvent: false }
        );

        this.setRssUrlValidators(res.social_tag || '');

        this.coreService
          .getData(`/contentsiterssfeed/${res.content_site_id}`)
          .pipe(take(1))
          .subscribe((feedsRes: any) => {
            this.feeds = feedsRes.data || [];
            const matchedFeed = this.feeds.find(
              (f) => f.id == res.rss_feed_id
            );
            this.postForm.patchValue(
              { rss_feed_id: matchedFeed ? matchedFeed.id : null },
              { emitEvent: false }
            );
          });

        if (res.image_input_url) {
          this.imagePreview = res.image_input_url;
        }

        if (res.image_array?.length) {
          this.uploadedFiles = res.image_array.map(
            (img: string, i: number) => ({
              file: null,
              preview: img,
              alt: res.alt_text_array?.[i] || '',
            })
          );
          this.urlsArry = [...res.image_array];
          this.updateFormArrays();
        }

        this.isDataLoaded = true;
        this.applyEditLock();
      });
  }

  initValueChanges(): void {
    this.postForm.get('site')?.valueChanges.subscribe((siteDomain: string) => {
      if (this.id) return;
      const selectedSite = this.sites.find((s) => s.Domain === siteDomain);
      if (!selectedSite) return;

      this.postForm.get('content_site_id')?.setValue(selectedSite.ID);

      this.feeds = [];
      this.filteredPlatforms = [];
      this.postForm.patchValue({
        platform: '',
        rss_feed_id: null,
        tag: '',
        rss_article_url: '',
        rss_description: '',
      });

      this.coreService
        .getData(`/contentsiterssfeed/${selectedSite.ID}`)
        .pipe(
          take(1),
          catchError(() => of({ data: [] }))
        )
        .subscribe((res: any) => {
          this.feeds = res.data || [];
          if (!this.id) {
            this.postForm.get('rss_feed_id')?.enable();
          }
        });

      this.filteredPlatforms = this.platforms.filter(
        (p) => p.site_id === selectedSite.ID
      );
      this.clearImages();
    });

    this.postForm.get('rss_feed_id')?.valueChanges.subscribe((id: number) => {
      const selectedFeed = this.feeds.find((f) => f.id === id);
      if (selectedFeed?.url) {
        this.postForm.patchValue({ rss_article_url: selectedFeed.url });
      }
    });

    this.postForm.get('platform')?.valueChanges.subscribe((platform: string) => {
      this.updateFilteredTagsByPlatform(platform);
      this.clearImages();
      this.previousTag = null;
    });

    this.postForm.get('tag')?.valueChanges.subscribe((newTag: string) => {
      this.filterPostStatusesByTag(newTag);
      this.setRssUrlValidators(newTag);

      const newIsSingle = this.singleImageTags.includes(newTag);
      const newIsReel = this.reelTags.includes(newTag);

      const prevIsSingle = this.previousTag
        ? this.singleImageTags.includes(this.previousTag)
        : false;
      const prevIsReel = this.previousTag
        ? this.reelTags.includes(this.previousTag)
        : false;

      if (!(newIsSingle && prevIsSingle) && !(newIsReel && prevIsReel)) {
        this.clearImages();
      }

      if (this.allVideoTags.includes(newTag)) {
        this.postForm.get('video_type')?.setValidators([Validators.required]);

        const platform = (
          this.postForm.get('platform')?.value || ''
        ).toLowerCase();

        if (!this.id && platform.includes('instagram')) {
          this.postForm.patchValue({ video_type: 'video_upload' });
        }
      }

      this.previousTag = newTag;
    });
  }

  loadSites() {
    return this.coreService
      .getData(`/querybuilder/websites-feeds-dropdown/${this.companyID}`)
      .pipe(
        take(1),
        map((res: any) => (res.data || []).filter((site: any) => site.hasFeed)),
        catchError(() => of([] as ContentSiteI[]))
      );
  }

  loadPlatforms() {
    return this.coreService
      .getData('/querybuilder/platform-dropdown')
      .pipe(
        take(1),
        map((res: any) =>
          (res.data as SocialPlatformI[]).filter(
            (p) => p.isApproved === 'Approved' && !!p.access_token
          )
        ),
        catchError(() => of([] as SocialPlatformI[]))
      );
  }

  private validateImage(file: File): string | null {
    if (file.size > this.MAX_IMAGE_SIZE) {
      return `Image "${file.name}" exceeds 5MB limit.`;
    }
    if (file.type === 'image/webp') {
      return 'WebP images are not supported. Please upload JPG or PNG.';
    }
    return null;
  }

  clearImages(): void {
    this.selectedFile = null;
    this.imagePreview = null;
    this.uploadedFiles.forEach((uf) => URL.revokeObjectURL(uf.preview));
    this.uploadedFiles = [];
    this.updateFormArrays();
  }

  dropImages(event: CdkDragDrop<any[]>): void {
    moveItemInArray(
      this.uploadedFiles,
      event.previousIndex,
      event.currentIndex
    );
    this.updateFormArrays();
  }

  clearAll(): void {
    this.clearImages();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) return;

    const file = input.files[0];
    const error = this.validateImage(file);
    if (error) {
      this.coreService.showSnackbar(error);
      input.value = '';
      return;
    }

    this.selectedFile = file;

    const reader = new FileReader();
    reader.onload = () => (this.imagePreview = reader.result as string);
    reader.readAsDataURL(file);
  }

  onVideoFileSelected(event: Event): void {
    if (this.isEditBlocked || !!this.id) return;

    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) return;

    const file = input.files[0];
    this.videoFileName = file.name;

    this.postForm.patchValue({
      video_file: file,
      video_url: null,
    });

    this.postForm.get('video_file')?.markAsTouched();
  }

  removeImage(): void {
    this.selectedFile = null;
    this.imagePreview = null;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onFilesDropped(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer?.files) {
      this.addFiles(event.dataTransfer.files);
    }
  }

  onMultipleFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    Array.from(input.files).forEach((file) => {
      const error = this.validateImage(file);
      if (error) {
        this.coreService.showSnackbar(error);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.uploadedFiles.push({ file, preview: e.target.result, alt: '' });
        this.updateFormArrays();
      };

      reader.readAsDataURL(file);
    });

    input.value = '';
  }

  addFiles(files: FileList): void {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const error = this.validateImage(file);
      if (error) {
        this.coreService.showSnackbar(error);
        continue;
      }

      const preview = URL.createObjectURL(file);
      this.uploadedFiles.push({ file, preview, alt: '' });
    }

    this.updateFormArrays();
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.updateFormArrays();
  }

  updateFormArrays(): void {
    this.postForm.patchValue({
      image_array: this.uploadedFiles.map((f) => f.file),
      alt_text_array: this.uploadedFiles.map((f) => f.alt),
    });
  }

  getFilePreview(file: File): string {
    return URL.createObjectURL(file);
  }

  submit(): void {
    const selectedSpecificTag = this.postForm.get('tag')?.value;
    this.setRssUrlValidators(selectedSpecificTag);
    if (this.postForm.invalid) {
      this.postForm.markAllAsTouched();
      this.coreService.showSnackbar('Please complete all required fields.');
      return;
    }

    const formRaw = this.postForm.getRawValue();

    const scheduledFor = formRaw.scheduled_for;
    if (scheduledFor) {
      const selectedDate = new Date(scheduledFor);
      const now = new Date();
      selectedDate.setSeconds(0, 0);
      now.setSeconds(0, 0);
      if (selectedDate.getTime() < now.getTime()) {
        this.coreService.showSnackbar(
          'The scheduled date and time cannot be in the past.'
        );
        return;
      }
    }

    const selectedTag = formRaw.tag;
    if (this.singleImageTags.includes(selectedTag)) {
      const hasSingleImage =
        !!this.selectedFile ||
        !!this.imagePreview ||
        this.uploadedFiles.length === 1;

      if (!hasSingleImage) {
        this.coreService.showSnackbar(
          'Please select a single image for this tag.'
        );
        return;
      }
    }

    if (this.mediaTags.includes(selectedTag)) {
      if (this.uploadedFiles.length < 4) {
        this.coreService.showSnackbar('You must upload at least 4 images.');
        return;
      }
      if (this.uploadedFiles.length > 10) {
        this.coreService.showSnackbar('You cannot upload more than 10 images.');
        return;
      }
    }

    const tag = formRaw.tag;
    const videoType = formRaw.video_type;

    if (this.allVideoTags.includes(tag)) {
      if (!videoType) {
        this.coreService.showSnackbar('Please select a video type.');
        return;
      }

      if (videoType === 'video_upload' && !formRaw.video_file && !this.id) {
        this.coreService.showSnackbar('Please upload a video file.');
        return;
      }

      if (videoType === 'video_url' && !formRaw.video_url) {
        this.coreService.showSnackbar('Please provide a YouTube link.');
        return;
      }
    }

    const socialPlatformId = formRaw.social_platform_id?.toString() || '';

    const formData = new FormData();
    formData.append('company_id', this.companyID.toString());
    formData.append(
      'content_site_id',
      formRaw.content_site_id?.toString() || ''
    );
    formData.append('rss_feed_id', formRaw.rss_feed_id?.toString() || '');
    formData.append('social_platform_id', socialPlatformId);
    formData.append('social_tag', formRaw.tag || '');
    formData.append('post_status', formRaw.post_status || '');
    formData.append('article_url', formRaw.rss_article_url || '');
    formData.append('rss_description', formRaw.rss_description || '');
    formData.append('post_title', formRaw.post_title || '');
    formData.append('scheduled_for', formRaw.scheduled_for || '');
    formData.append('is_altai', formRaw.is_altai ? '1' : '0');
    formData.append('text_direction', this.textDirection);

    formData.append('video_type', formRaw.video_type || '');

    if (formRaw.video_type === 'video_upload' && formRaw.video_file) {
      formData.append('video_file', formRaw.video_file);
    }

    if (formRaw.video_type === 'video_url' && formRaw.video_url) {
      formData.append('video_url', formRaw.video_url);
    }

    if (formRaw.video_type_id) {
      formData.append('video_type_id', formRaw.video_type_id.toString());
    }

    if (this.selectedFile) {
      formData.append('image_input', this.selectedFile);
    } else if (this.imagePreview) {
      formData.append('image_input', this.imagePreview);
    }

    const newFileNames: string[] = [];
    if (this.uploadedFiles.length > 0) {
      this.uploadedFiles.forEach((uf, i) => {
        if (uf.file) {
          formData.append(`image_array[${i}]`, uf.file);
          newFileNames.push(uf.file.name);
        }
        formData.append(`alt_text_array[${i}]`, uf.alt || '');
      });
    }

    const currentFiles = this.uploadedFiles
      .filter((uf) => !uf.file)
      .map((uf) => {
        const parts = uf.preview.split('/');
        return parts[parts.length - 1];
      });

    const urlArray = [...currentFiles, ...newFileNames];
    formData.append('url_array', JSON.stringify(urlArray));

    const endpoint = `manual-post/updatePost/${this.id}`;

    this.coreService
      .postData(endpoint, formData)
      .pipe(
        take(1),
        catchError((err) => {
          this.coreService.showSnackbar('An error occurred.');
          console.error(err);
          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.coreService.showSnackbar('Post updated successfully!');

          this.onSubmitClbk.emit(res);

          this.postForm.reset();
          this.feeds = [];
          this.filteredPlatforms = [];
          this.filteredTags = [];
          this.selectedFile = null;
          this.imagePreview = null;
          this.uploadedFiles = [];

          this.router.navigate([
            '/preview-post/' + res.social_tag + '/' + res.post_id,
          ]);
        }
      });
  }

  private loadSidebarPosts(): void {
    if (typeof window === 'undefined') {
      this.posts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);

    if (!raw) {
      this.posts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Post[] | null;
      this.posts = Array.isArray(parsed)
        ? parsed.slice(0, this.maxSidebarCards)
        : [];
    } catch (e) {
      console.error('Failed to parse latest posts from localStorage', e);
      this.posts = [];
    }
  }

  private loadAlertsFromStorage(): void {
    if (typeof window === 'undefined') return;

    const raw = localStorage.getItem(this.LATEST_ALERTS_KEY);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.alerts = parsed.slice(0, this.maxSidebarCards);
      }
    } catch (e) {
      console.error('Failed to parse alerts from storage', e);
    }
  }
  
  loadActions(id: string): void {
    this.coreService
      .getData(`/post/actions/${this.companyID}/${id}`)
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          if (!res) return;

          this.enableRepeat = res.enable_repeat ?? null;
          this.postStatus = res.post_status ?? '';
          this.enableDelete = 1;

          this.nextId = res.next_id ?? null;
          this.prevId = res.prev_id ?? null;
          this.applyEditLock();
        },
        error: (err: any) => console.error('Failed to load post actions:', err),
      });
  }

  nextPost(): void {
    if (!this.nextId) return;
    this.router.navigate(['/edit-post', this.nextId]);
  }

  prevPost(): void {
    if (!this.prevId) return;
    this.router.navigate(['/edit-post', this.prevId]);
  }

  backToPrevPage(): void {
    this.location.back();
  }

  viewList() {
    this.router.navigate(['/posts']);
  }

  openDeleteConfirm(): void {
    this.dialog.open(DialogComponent, {
      data: {
        content: this.confirmDeleteTmpl,
        header: 'Delete post?',
        cls: 'bg-red-500 !text-white',
      },
    });
  }

  deletePost(): void {
    if (!this.id) return;

    this.coreService
      .postData(`manual-post/deletePost/${this.id}`, {
        company_id: this.companyID,
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.dialog.closeAll();
          this.coreService.showSnackbar('Post deleted successfully');
          this.router.navigate(['/posts']);
        },
        error: (err) => {
          console.error('Delete post error:', err);
          this.dialog.closeAll();
          this.coreService.showSnackbar('You cannot delete this post');
        },
      });
  }

  private applyEditLock(): void {
    if (!this.postForm) return;

    if (this.isEditBlocked) {
      this.postForm.disable({ emitEvent: false });
      return;
    }

    this.postForm.enable({ emitEvent: false });

    this.postForm.get('site')?.disable({ emitEvent: false });
    this.postForm.get('rss_feed_id')?.disable({ emitEvent: false });
    this.postForm.get('platform')?.disable({ emitEvent: false });
    this.postForm.get('tag')?.disable({ emitEvent: false });
    this.postForm.get('video_type')?.disable({ emitEvent: false });

    this.updateScheduledForState(
      this.postForm.get('post_status')?.value
    );
  }

  carouselImageIndex = 0;

  prevCarouselImage(): void {
    if (!this.uploadedFiles?.length) return;

    this.carouselImageIndex =
      (this.carouselImageIndex - 1 + this.uploadedFiles.length) % this.uploadedFiles.length;
  }

  nextCarouselImage(): void {
    if (!this.uploadedFiles?.length) return;

    this.carouselImageIndex =
      (this.carouselImageIndex + 1) % this.uploadedFiles.length;
  }

  goToCarouselImage(index: number): void {
    if (!this.uploadedFiles?.length) return;

    this.carouselImageIndex = index;
  }

  removeCarouselImage(): void {
    if (!this.uploadedFiles?.length) return;

    this.removeFile(this.carouselImageIndex);

    const length = this.uploadedFiles.length;

    if (length === 0) {
      this.carouselImageIndex = 0;
      return;
    }

    if (this.carouselImageIndex >= length) {
      this.carouselImageIndex = length - 1;
    }
  }

}

